/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/<domain>/<domain>.contract.ts` (or the
 * shared bases under `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This
 * file exists so the web SPA can import zod schemas + oRPC contract
 * types from `@event-manager/api-client/contracts/<domain>.contract`
 * without reaching into the server workspace.
 */

/**
 * System Agent Contract - AI chat streaming endpoints
 *
 * This contract defines the API for direct AI chat without WhatsApp.
 * The backend talks directly to AI via OpenRouter and streams responses
 * using AI SDK UI stream protocol (SSE).
 *
 * Streaming Implementation Note:
 * The chat endpoint returns an initial response with streamId, but the actual
 * streaming is done via SSE (Server-Sent Events). In the oRPC procedure
 * implementation, use `eventIterator` for the streaming response.
 */
import { eventIterator, oc } from "@orpc/contract";
import * as z from "zod";
import { commonErrors } from "./base";
import { AiMessageMetricsSchema } from "./conversations.contract";

// ============================================================================
// Message Schemas (AI SDK UI Message format)
// ============================================================================

/**
 * Text part schema - plain text content
 */
const TextPartSchema = z.object({
	type: z.literal("text"),
	text: z.string().min(1).max(10_000),
});

/**
 * File-part URL — accepts absolute http(s) URLs, `data:` URIs, AND internal
 * relative media paths (`/api/orgs/:orgId/media/:assetId`). Voice notes and
 * other attachments are now uploaded to S3 and referenced by their auth-gated
 * same-origin media path, which is not a valid absolute URL (so plain
 * `z.string().url()` would 400 it). Kept strict enough to reject junk.
 */
const HTTP_URL_PREFIX_REGEX = /^https?:\/\//;
const FilePartUrlSchema = z
	.string()
	.min(1)
	.refine(
		(v) =>
			v.startsWith("data:") ||
			v.startsWith("/api/orgs/") ||
			HTTP_URL_PREFIX_REGEX.test(v),
		"must be an http(s) URL, data: URI, or internal media path"
	);

/**
 * File part schema - file attachment with URL reference
 * Matches AI SDK FileUIPart interface
 */
const FilePartSchema = z.object({
	type: z.literal("file"),
	mediaType: z
		.string()
		.trim()
		.max(255)
		.regex(/^[a-z\d!#$&^_.+-]+\/[a-z\d!#$&^_.+-]+$/i),
	filename: z.string().max(255).nullish(),
	url: FilePartUrlSchema,
});

/**
 * Part schema union for message parts
 */
const PartSchema = z.union([TextPartSchema, FilePartSchema]);

/**
 * User message schema - for single new user message (strict validation)
 */
const UserMessageSchema = z.object({
	id: z.string().min(1),
	role: z.literal("user"),
	parts: z.array(PartSchema),
});

/**
 * Generic message schema - for tool approval flows.
 *
 * Parts use a passthrough object schema because AI SDK's UIMessagePart
 * includes types beyond SystemAgentUiMessagePart (e.g. SourceDocumentUIPart,
 * StepStartUIPart). These parts are passed through to AI SDK's
 * convertToModelMessages without server-side interpretation.
 *
 * .passthrough() is intentional here: the server forwards these parts to
 * the AI SDK without inspecting them, and the AI SDK defines additional
 * part types that we cannot enumerate ahead of time.
 */
const GenericMessageSchema = z.object({
	id: z.string(),
	role: z.string(),
	parts: z.array(z.object({ type: z.string() }).passthrough()),
});

// ============================================================================
// Stream Event Schemas (AI SDK data stream protocol)
// ============================================================================

/**
 * Stream chunk types matching AI SDK UI stream protocol.
 *
 * CRITICAL: These types MUST match the AI SDK's UIMessageChunk format exactly.
 * The AI SDK's useChat hook with DefaultChatTransport expects these types:
 * - 'start' with messageId
 * - 'text-start' with id
 * - 'text-delta' with id and delta (NOT 'text' with 'text')
 * - 'text-end' with id
 * - 'reasoning-start', 'reasoning-delta', 'reasoning-end' with id and delta
 * - 'tool-call' (legacy) or 'tool-input-available' with toolCallId, toolName, input
 * - 'tool-result' (legacy) or 'tool-output-available' with toolCallId, output
 * - 'finish' (not 'done')
 * - 'error' with errorText (not 'error')
 * - custom data parts like 'data-stream-id'
 *
 * Reference: https://sdk.vercel.ai/docs/ai-sdk-ui/stream-protocol
 */
export const StreamChunkSchema = z.discriminatedUnion("type", [
	// Message lifecycle
	z.object({
		type: z.literal("start"),
		messageId: z.string().optional(),
		// AI SDK attaches this to message.metadata when the stream starts
		messageMetadata: z
			.object({
				createdAt: z.string(),
			})
			.optional(),
	}),
	z.object({ type: z.literal("finish") }),

	// Text streaming (AI SDK v6 format)
	z.object({ type: z.literal("text-start"), id: z.string() }),
	z.object({
		type: z.literal("text-delta"),
		id: z.string(),
		delta: z.string(),
	}),
	z.object({ type: z.literal("text-end"), id: z.string() }),

	// Reasoning streaming (AI SDK v6 format)
	z.object({ type: z.literal("reasoning-start"), id: z.string() }),
	z.object({
		type: z.literal("reasoning-delta"),
		id: z.string(),
		delta: z.string(),
	}),
	z.object({ type: z.literal("reasoning-end"), id: z.string() }),

	// Generated image/audio output.
	z.object({
		type: z.literal("file"),
		mediaType: z.string(),
		url: z.string(),
	}),

	// Tool calls (AI SDK v6 format)
	z.object({
		type: z.literal("tool-input-available"),
		toolCallId: z.string(),
		toolName: z.string(),
		input: z.record(z.string(), z.unknown()).nullish(),
	}),
	z.object({
		type: z.literal("tool-output-available"),
		toolCallId: z.string(),
		output: z.record(z.string(), z.unknown()).nullish(),
	}),
	z.object({
		type: z.literal("tool-output-error"),
		toolCallId: z.string(),
		errorText: z.string(),
	}),
	z.object({
		type: z.literal("tool-approval-request"),
		approvalId: z.string(),
		toolCallId: z.string(),
	}),

	// Error handling
	z.object({
		type: z.literal("error"),
		errorText: z.string(),
		code: z.literal("daily_message_limit_reached").optional(),
		limit: z.number().int().positive().optional(),
		remaining: z.number().int().nonnegative().optional(),
		resetsAt: z.string().datetime().optional(),
	}),

	// Custom data parts (used for stream metadata)
	z.object({
		type: z.literal("data-stream-id"),
		data: z.object({ streamId: z.string() }),
	}),
	// AI generation diagnostics (speed, tokens, cost) emitted once at stream
	// end via `channel.writeData('usage', ...)`. Carried on the direct stream
	// so the sender tab can render the per-response metrics chip immediately.
	z.object({
		type: z.literal("data-usage"),
		data: AiMessageMetricsSchema,
	}),
]);

// ============================================================================
// Availability Schema
// ============================================================================

export const AiAvailabilitySchema = z.object({
	available: z.boolean(),
	reason: z.string().optional(),
});

// ============================================================================
// Request/Response Schemas
// ============================================================================

/**
 * Streaming chat request schema
 * Matches AI SDK useChat request format
 *
 * PERSISTENCE CONTRACT:
 * - When `message` is set: The new user message is persisted automatically.
 * - When `messages` is set AND `newUserMessageId` is set: The message with
 *   that ID is found in `messages` and persisted.
 * - When `messages` is set WITHOUT `newUserMessageId`: No user message is
 *   persisted (tool approval continuation with no new user input).
 */
const StreamingChatInputSchema = z.object({
	// Conversation ID (AI SDK useChat id)
	id: z.string().min(1),
	// Single new message mode (strict validation for text/file parts)
	message: UserMessageSchema.nullish(),
	// Full history mode for tool approval flows (permissive)
	messages: z.array(GenericMessageSchema).nullish(),
	// When using `messages` mode AND there's a new user message to persist,
	// specify its ID here. The backend will find it in `messages` and persist it.
	// This field makes persistence explicit and type-safe.
	newUserMessageId: z.string().nullish(),
	// Model configuration
	model: z.string().trim().nullish(),
	selectedChatModel: z.string().nullish(),
	// Optional stream ID for resumable streams
	stream_id: z.string().nullish(),
	attachments: z
		.array(
			z
				.object({
					stagedDocumentId: z.string().regex(/^\d+$/),
					filename: z.string().min(1).max(255),
				})
				.strict()
		)
		.max(5)
		.optional(),
});

/**
 * Rewrite request schema — multi-turn, non-persistent text transformation
 *
 * Used by the in-chat AI rewrite popup. The server applies `instruction`
 * to `text` (the original selection) optionally building on a `history`
 * of prior (instruction, result) turns from the same iteration session.
 *
 * The conversation is NOT persisted to any conversation channel — it
 * lives only inside this single call. The client carries the history
 * back on each follow-up turn so the model has full context for
 * iterative refinement (e.g. "make this professional" → result → "now
 * shorten it" → result' → "remove the second sentence" → ...).
 */
const RewriteTurnSchema = z.object({
	instruction: z.string().min(1).max(2000),
	result: z.string().min(1).max(20_000),
});
const RewriteContextMessageSchema = z.object({
	role: z.enum(["user", "assistant"]),
	text: z.string().min(1).max(4000),
});

const RewriteTextInputSchema = z.object({
	// Full-document rewrites (e.g. the system-prompt editor rewriting the whole
	// prompt) send the entire document as `text`, so the ceiling matches
	// `documentContext` — both can carry the whole document.
	text: z.string().min(1).max(40_000),
	instruction: z.string().min(1).max(2000),
	// 'rewrite' (default) transforms `text`; 'generate' composes a fresh
	// message from `text` (a short description) using a composer prompt.
	mode: z.enum(["rewrite", "generate"]).optional(),
	history: z.array(RewriteTurnSchema).max(20).optional(),
	language: z.enum(["en", "ar"]).optional(),
	context: z.array(RewriteContextMessageSchema).max(100).optional(),
	documentContext: z.string().max(40_000).optional(),
	preserveVoice: z.boolean().optional(),
});

const RewriteTextOutputSchema = z.object({
	rewrittenText: z.string(),
});

/**
 * Tool denial request schema
 */
const ToolDenialInputSchema = z.object({
	messageId: z.string().min(1),
	toolCallId: z.string().min(1),
	toolName: z.string().min(1),
	input: z.record(z.string(), z.unknown()).nullish(),
});

/**
 * Schema for the active stream snapshot response.
 * Used by late-joining clients to catch up on an in-progress stream.
 *
 * parts use .passthrough() because this is OUTPUT-only -- the server
 * returns accumulated AI SDK message parts whose shapes vary by type.
 */
const StreamSnapshotOutputSchema = z.object({
	active: z.boolean(),
	streamId: z.string().optional(),
	messageId: z.string().optional(),
	parts: z.array(z.object({ type: z.string() }).passthrough()).optional(),
	seq: z.number().int().optional(),
});

// ============================================================================
// Contract Definition
// ============================================================================

export const systemAgentContract = {
	/**
	 * Check if AI chat service is available (OpenRouter key configured)
	 */
	checkAvailability: oc
		.route({
			method: "GET",
			path: "/api/system-agent/availability",
			summary: "Check AI chat availability",
			description:
				"Check if the AI chat service is configured and available for the current tenant.",
			tags: ["System Agent"],
		})
		.output(AiAvailabilitySchema),

	/**
	 * Send message to system agent with streaming response
	 *
	 * Response is streamed via SSE (Server-Sent Events).
	 * In oRPC procedure implementation, use eventIterator:
	 *
	 * @example
	 * ```typescript
	 * import { eventIterator } from '@orpc/server'
	 *
	 * .output(eventIterator(StreamChunkSchema))
	 * .handler(async function* ({ input, signal }) {
	 *   // ... yield stream chunks
	 * })
	 * ```
	 */
	chat: oc
		.route({
			method: "POST",
			path: "/api/system-agent/chat",
			summary: "Send message to system agent (streaming)",
			description:
				"Send a message directly to the AI assistant. Response is streamed via SSE using AI SDK UI stream protocol.",
			tags: ["System Agent"],
		})
		// Per-caller rate limit — mirrors ai-chatbot `lib/ratelimit.ts`. The
		// oRPC rateLimitMiddleware (already concat'd into publicProcedure)
		// reads this meta and enforces a Redis Lua-atomic token bucket keyed
		// on (apikey > bearer token > oauth client > ip). Fails open if
		// Redis is unavailable.
		.meta({
			rateLimit: {
				name: "system-agent.chat",
				limit: 20,
				ttl: 60,
			},
		})
		.input(StreamingChatInputSchema)
		// Use eventIterator for streaming response - yields StreamChunk events
		.output(eventIterator(StreamChunkSchema)),

	/**
	 * Resume an active UI stream
	 *
	 * AI SDK compatible stream resume endpoint.
	 * Returns HTTP 204 if no active stream exists.
	 */
	resumeStream: oc
		.route({
			method: "GET",
			path: "/api/system-agent/chat/{chatId}/stream",
			summary: "Resume system agent UI stream",
			description:
				"AI SDK compatible stream resume endpoint. Returns HTTP 204 if no active stream.",
			tags: ["System Agent"],
		})
		.input(
			z.object({
				chatId: z.string().min(1),
				resumeAt: z.coerce.number().int().min(0).optional(),
			})
		)
		// Note: Returns SSE stream or 204 No Content
		.output(z.object({ resumed: z.boolean() })),

	/**
	 * Cancel an active stream
	 *
	 * Aborts the AI generation for the specified stream.
	 */
	cancelStream: oc
		.route({
			method: "POST",
			path: "/api/system-agent/chat/{streamId}/cancel",
			summary: "Cancel an active stream",
			description: "Abort the AI generation for the specified stream",
			tags: ["System Agent"],
		})
		.input(
			z.object({
				streamId: z.string().min(1),
			})
		)
		.output(
			z.object({
				cancelled: z.boolean(),
			})
		)
		.errors({
			NOT_FOUND: {
				message: "Stream not found or already completed",
			},
		}),

	/**
	 * Rewrite a piece of text using the configured AI model.
	 *
	 * One-shot, non-streaming, NOT persisted to any conversation. Used by
	 * the chat composer's selection-popup AI rewrite (Summarize / Make
	 * Professional / etc.). Tenant-scoped via `getTenantSecret` inside
	 * `createAIProvider()`.
	 */
	rewriteText: oc
		.route({
			method: "POST",
			path: "/api/system-agent/rewrite",
			summary: "Rewrite selected text via AI (one-shot)",
			description:
				'Apply a free-form instruction (or preset like "Summarize") to the given text and return the rewritten text. Not persisted.',
			tags: ["System Agent"],
		})
		.meta({
			rateLimit: {
				name: "system-agent.rewrite-text",
				limit: 20,
				ttl: 60,
			},
		})
		.input(RewriteTextInputSchema)
		.output(RewriteTextOutputSchema)
		.errors({
			AI_UNAVAILABLE: {
				message: "AI service is not configured",
			},
			GENERATION_FAILED: {
				message: "Failed to generate rewritten text",
			},
		}),

	/**
	 * Delete trailing messages after a given message
	 *
	 * Used for edit/regenerate flows. Deletes the specified message
	 * and any messages after it.
	 */
	deleteTrailingMessages: oc
		.route({
			method: "POST",
			path: "/api/system-agent/actions/messages/{messageId}/cleanup",
			summary: "Delete trailing messages after a given message",
			description:
				"Deletes the specified message and any messages after it. Used for edit/regenerate flows.",
			tags: ["System Agent"],
		})
		.input(
			z.object({
				messageId: z.string().min(1),
				editedContent: z.string().trim().optional(),
			})
		)
		.output(
			z.object({
				deleted: z.number().int().min(0),
			})
		)
		.errors({
			INVALID_MESSAGE_ID: {
				message: "Invalid message ID format",
			},
			...commonErrors,
		}),

	/**
	 * Get a snapshot of an active stream for mid-stream join.
	 *
	 * Returns the current accumulated parts and sequence number so a
	 * late-joining client can render the partial response immediately
	 * and then continue receiving live chunks.
	 */
	getActiveStreamSnapshot: oc
		.route({
			method: "GET",
			path: "/api/system-agent/stream-snapshot/{conversationId}",
			summary: "Get active stream snapshot",
			description:
				"Returns accumulated parts from an active stream so a late-joining client can catch up instantly.",
			tags: ["System Agent"],
		})
		.input(
			z.object({
				conversationId: z.string().min(1),
			})
		)
		.output(StreamSnapshotOutputSchema),

	/**
	 * Persist tool denial
	 *
	 * Called when user denies a tool execution.
	 * Persists the denial to the database immediately since
	 * sendAutomaticallyWhen doesn't trigger for denied tools.
	 */
	persistToolDenial: oc
		.route({
			method: "POST",
			path: "/api/system-agent/tool-denial",
			summary: "Persist tool denial",
			description:
				"Persists a tool denial to the database immediately. Called when user denies a tool.",
			tags: ["System Agent"],
		})
		.input(ToolDenialInputSchema)
		.output(
			z.object({
				persisted: z.boolean(),
			})
		)
		.errors({
			MISSING_FIELDS: {
				message: "Missing required fields: messageId, toolCallId, toolName",
			},
			PERSIST_DENIAL_FAILED: {
				message: "Failed to persist tool denial",
			},
		}),
};

// ============================================================================
// Type Exports
// ============================================================================

export type SystemAgentContract = typeof systemAgentContract;
export type AiAvailability = z.infer<typeof AiAvailabilitySchema>;
export type StreamChunk = z.infer<typeof StreamChunkSchema>;
export type StreamingChatInput = z.infer<typeof StreamingChatInputSchema>;
export type ToolDenialInput = z.infer<typeof ToolDenialInputSchema>;
export type RewriteTextInput = z.infer<typeof RewriteTextInputSchema>;
export type RewriteTextOutput = z.infer<typeof RewriteTextOutputSchema>;
export type RewriteContextMessage = z.infer<typeof RewriteContextMessageSchema>;
export type StreamSnapshot = z.infer<typeof StreamSnapshotOutputSchema>;
export type TextPart = z.infer<typeof TextPartSchema>;
export type FilePart = z.infer<typeof FilePartSchema>;
export type UserMessage = z.infer<typeof UserMessageSchema>;
